import { Payment } from '@mui/icons-material';
import { ReactElement, useCallback } from 'react';
import { Button, useCreate, useGetMany, useListContext, useNotify } from 'react-admin';
import type { BillingItemResource } from '../../../Entities/BillingItem';
import { idFrom, not } from '../../../utils';
import type { MerchantResource } from '../../../Entities/Merchant';
import { isNil } from 'lodash';
import type { PaymentResource } from '../../../Entities/Payment';
import billingItemsToPayment from './billingItemToPayment';
import validateBillingItems from './validateBillingItems';
import { PayAccountResource } from '../../../Entities/PayAccount';

export default function SecurePayButton(): ReactElement {
  const { selectedIds, onUnselectItems, refetch } = useListContext();
  const [create] = useCreate<PaymentResource>();
  const notify = useNotify();

  const billingItems = useGetMany<BillingItemResource>('billing_items', {
    ids: selectedIds,
    meta: { prefetch: ['merchant', 'merchant.pay_account'] },
  });

  const merchantIds = billingItems.data?.map((bi) => idFrom(bi, 'merchant')).filter(not(isNil));
  const merchants = useGetMany<MerchantResource>('merchants', { ids: merchantIds ?? [] });
  const payAccounts = useGetMany<PayAccountResource>('pay_accounts', {
    ids: merchants.data?.map((m) => idFrom(m, 'pay_account')).filter(not(isNil)) ?? [],
  });

  const errors = validateBillingItems(billingItems.data ?? []);
  const payment = billingItemsToPayment(billingItems.data ?? [], merchants.data ?? []);

  const isDisabled = billingItems.isLoading || merchants.isLoading || payAccounts.isLoading;

  const handleClick = useCallback(() => {
    if (isDisabled) {
      return;
    }

    if (errors) {
      notify(`Cannot charge payment:\n • ${errors.join('\n • ')}`, {
        type: 'error',
        multiLine: true,
      });

      return;
    }

    create(
      'payments',
      { data: payment },
      {
        onError: (error) => notify((error as Error).message, { type: 'error' }),
        onSettled: () => refetch(),
        onSuccess: () => {
          notify('Payment charged successfully', { type: 'info' });
          onUnselectItems();
        },
      },
    );
  }, [create, errors, isDisabled, notify, onUnselectItems, payment, refetch]);

  return (
    <Button label="Charge Payment (SecurePay)" onClick={handleClick} disabled={isDisabled}>
      <Payment />
    </Button>
  );
}
